import { DownOutlined } from '@ant-design/icons';
import { Avatar, Button, ButtonProps, Typography } from 'antd';
import clx from 'classnames';
import React from 'react';
import styled from 'styled-components';
import { getNameInitials } from '../../utils/getNameInitials';
const { Text, Paragraph } = Typography;

const StyledUserButton = styled(Button)`
  display: flex;
  flex: 0 1 auto;
  min-width: 0;
  height: auto;
  padding: 4px;
  padding-right: 8px;
  border-radius: 23px;
  border: none;
  color: ${props => props.theme.colorText};
  opacity: 1;
  &.ant-btn-link:not(:disabled):not(.ant-btn-disabled) {
    color: ${props => props.theme.colorText};
    opacity: 1;
    &:active,
    &:hover {
      color: ${props => props.theme.colorPrimaryHover};
      background-color: ${props => props.theme.colorFillContent};
    }
  }
  .ant-avatar {
    font-family: ${props => props.theme.fontFamily};
    background-color: ${props => props.theme.colorPrimary};
  }
  &:hover {
    .ant-avatar {
      background-color: ${props => props.theme.colorPrimaryHover};
    }
  }

  &:not(:disabled):focus-visible {
    outline-offset: 0 !important;
  }
`;

const CompactStyledUserButton = styled(StyledUserButton)`
  padding: 4px;
  .ant-avatar {
    margin-right: 4px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
`;

const AvatarUser = styled(Avatar)`
  display: flex;
  flex: 0 0 38px;
  min-width: 0;
`;
const UserTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 270px;
  text-align: left;
  line-height: 1.3;
  padding: 0 8px;
`;
const UserNameLine = styled(Text)`
  display: inline-block;
  color: inherit;
  line-height: 1.3;
`;

const LineParagraph = styled(Paragraph)`
  font-weight: 400;
  font-size: 0.9em;
  line-height: 1.3;

  display: inline-block;
  flex: 0 1 auto;
  min-width: 0;
  margin-top: 0;
  color: inherit;
  &.ant-typography {
    margin-bottom: 0;
  }
`;
const ArrowIcon = styled(DownOutlined)`
  flex: 0 0 auto;
  min-width: 0;
`;

export interface IUserButtonBaseProps extends Omit<ButtonProps, 'type'> {
  compact?: boolean;
  name?: string;
  role?: string;
}

export const UserMenuAccessibilityClassName = 'user-menu-button--focus';

/**
 * Bottone personalizzato per il menu utente
 * Visualizza il nome, il ruolo dell'utente loggato.
 */
export function UserButtonBase(props: IUserButtonBaseProps) {
  const { compact, name, role, ...buttonProps } = props;

  const initials = getNameInitials(name);

  if (compact)
    return (
      <CompactStyledUserButton aria-haspopup type="link" {...buttonProps}>
        <ContentWrapper title={`${name ?? ''} - ${role ?? ''}`}>
          <AvatarUser aria-hidden size={38}>
            {initials}
          </AvatarUser>
          <ArrowIcon aria-hidden />
        </ContentWrapper>
      </CompactStyledUserButton>
    );

  return (
    <StyledUserButton
      aria-haspopup
      type="link"
      {...buttonProps}
      className={clx(buttonProps.className, UserMenuAccessibilityClassName)}
    >
      <ContentWrapper>
        <AvatarUser aria-hidden size={38}>
          {initials}
        </AvatarUser>
        <UserTextWrapper>
          <UserNameLine
            strong
            ellipsis
            title={`${name ?? ''}${role ? ' - ' + role : ''}`}
          >
            {name}
          </UserNameLine>
          {role && (
            <LineParagraph ellipsis title={role}>
              {role}
            </LineParagraph>
          )}
        </UserTextWrapper>
        <ArrowIcon aria-hidden />
      </ContentWrapper>
    </StyledUserButton>
  );
}
